import React, { useEffect, useState } from 'react';
import { generateApiKey, getApiKeyInfo } from '../../utils/api/APIKeyAPI';
import { motion } from 'framer-motion';
import { Key, Lock } from 'lucide-react';
import { useSelector } from 'react-redux';

const ApiKeyComponent = () => {
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [infoApiData, setInfoApiData] = useState({});
  const [error, setError] = useState(null);

  const orgIsSubscribed = useSelector(
    (state) => state?.orgData?.orgData?.organization?.isSubscribed
  );

  useEffect(() => {
    const fetchKeyInfo = async () => {
      try {
        const data = await getApiKeyInfo();
        setInfoApiData(data);
      } catch (err) {
        console.error('Failed to fetch API key info:', err);
        setError('Failed to load API key info.');
      }
    };

    fetchKeyInfo();
  }, []);

  const copyToClipboard = (apiKey, index) => {
    navigator.clipboard.writeText(apiKey)
      .then(() => {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
      })
      .catch((err) => {
        console.error('Failed to copy API key:', err);
      });
  };

  const createApiKey = async () => {
    try {
      setLoading(true);
      const response = await generateApiKey();

      if (response?.apiKey) {
        const newKey = { api_key: response.apiKey };
        setApiData([newKey]);
        setInfoApiData({ apiKey: { hasKey: true } });
        setError(null);
      } else {
        setError('Failed to generate API key.');
      }
    } catch (err) {
      console.error('Error generating API key:', err);
      setError('Unexpected error occurred while generating key.');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = (data) => {
    const csvRows = [['API Key'], ...data.map((item) => [item.api_key])];
    const csvContent = csvRows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'api_keys.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="card p-4 radius-12 position-relative"
      style={{
        minHeight: '90vh',
        opacity: orgIsSubscribed ? 1 : 0.6,
        pointerEvents: orgIsSubscribed ? 'auto' : 'none',
      }}
    >
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}

      {apiData.length === 0 ? (
        <div
          style={{
            minHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Key size={100} color="#6b7280" strokeWidth={1.5} />
          <h1 className="text-sm pt-4 pb-3" style={{ color: '#9ca3af' }}>
            Securely Generate Your API Key
          </h1>
          <p className="text-m mb-1" style={{ color: 'var(--primary-200)' }}>
            API keys are hashed and cannot be retrieved once generated.
            If you need a new key, use the regenerate option.
          </p>
          <p className="text-m mb-1" style={{ color: 'var(--danger-300)' }}>
            Note: Regenerating the API key will invalidate the previous one.
          </p>
          <p className="text-m mb-1" style={{ color: '#9ca3af' }}>
            Use this key to authenticate server-to-server communication.
          </p>
          <p className="text-m mb-4" style={{ color: '#9ca3af' }}>
            Keep it private and never expose it in public code.
          </p>

          <div className="d-flex flex-wrap justify-content-center gap-3 mt-3">
            <button
              type="button"
              className="btn btn-primary-400"
              onClick={createApiKey}
              disabled={loading}
            >
              {loading
                ? 'Creating...'
                : infoApiData?.apiKey?.hasKey
                ? 'Regenerate Key'
                : 'Generate Key'}
            </button>

            <a
              className="btn btn-outline-primary"
              href="https://docs.callhq.ai/api-reference/introduction"
              target="_blank"
              rel="noopener noreferrer"
            >
              API Key Guide
            </a>
          </div>
        </div>
      ) : (
        apiData.map((apiItem, index) => (
          <div key={index} className="card shadow-sm rounded-4 m-3">
            <div className="card-header bg-light d-flex align-items-center gap-2 fw-semibold fs-6 text-dark mb-3">
              <Key size={20} stroke="#1a73e8" />
              Your Private API KEY
            </div>

            <div className="p-3 pt-0">
              <label className="form-label text-muted small fw-medium mb-2">
                API Key <span className="text-danger">*</span>
              </label>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex gap-2">
                  <input
                    type="password"
                    className="form-control"
                    value={apiItem.api_key}
                    readOnly
                    style={{
                      maxWidth: '1000px',
                      flexGrow: 2,
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      padding: '8px',
                    }}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={() => copyToClipboard(apiItem.api_key, index)}
                    disabled={copiedIndex === index}
                  >
                    {copiedIndex === index ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => downloadCSV([apiItem])}
                  >
                    Download
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={createApiKey}
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Regenerate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))
      )}

      {/* Lock Overlay */}
      {!orgIsSubscribed && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
       
            pointerEvents: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            borderRadius: '12px',
          }}
        >
          <Lock size={32} color="#333" />
          <div className="mt-2 text-muted" style={{ fontSize: '14px' }}>
            Upgrade your plan to manage API keys.
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ApiKeyComponent;
